import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import * as React from 'react';
import { useTranslations } from '../../hooks/use_translations.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { MicroToast } from '../MicroToast/MicroToast.js';
import { ActivateToEditReadView } from './components/ActivateToEditReadView.js';
import { ActivateToEditValidationContainer } from './components/ActivateToEditValidationContainer.js';
import { ActivateToEditWriteView } from './components/ActivateToEditWriteView.js';
import { A2E_DATA_TESTIDS } from './constants.js';
import { jsx } from 'react/jsx-runtime';

var ActivateToEdit = function ActivateToEdit(_ref) {
  var ariaLabelWriteView = _ref.ariaLabelWriteView,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    hasPlaceholder = _ref.hasPlaceholder,
    _onBlur = _ref.onBlur,
    onCancel = _ref.onCancel,
    onChange = _ref.onChange,
    onConfirm = _ref.onConfirm,
    onValidate = _ref.onValidate,
    _ref$readOnly = _ref.readOnly,
    readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly,
    readView = _ref.readView,
    value = _ref.value,
    writeView = _ref.writeView;
  var _useThemeContext = useThemeContext(),
    localization = _useThemeContext.localization;
  var _useTranslations = useTranslations(localization === null || localization === void 0 ? void 0 : localization.translations),
    t = _useTranslations.t;
  var _React$useState = React.useState(value),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    state = _React$useState2[0],
    setState = _React$useState2[1];
  var _React$useState3 = React.useState(""),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    ariaLabelPrefix = _React$useState4[0],
    setAriaLabelPrefix = _React$useState4[1];
  var _React$useState5 = React.useState(false),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    isEditing = _React$useState6[0],
    setIsEditing = _React$useState6[1];
  var _React$useState7 = React.useState(),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    validationResponse = _React$useState8[0],
    setValidationResponse = _React$useState8[1];
  var prevIsEditing = React.useRef(false);
  var containerRef = React.useRef(null);
  var focusRef = /*#__PURE__*/React.createRef();
  var readViewFocusRef = React.useRef(null);
  React.useEffect(function () {
    setState(value);
  }, [value]);
  React.useEffect(function () {
    var _focusRef$current;
    (_focusRef$current = focusRef.current) === null || _focusRef$current === void 0 ? void 0 : _focusRef$current.focus();
  }, [focusRef]);
  React.useEffect(function () {
    if (prevIsEditing.current && !isEditing) {
      var _readViewFocusRef$cur;
      (_readViewFocusRef$cur = readViewFocusRef.current) === null || _readViewFocusRef$cur === void 0 ? void 0 : _readViewFocusRef$cur.focus();
    }
    prevIsEditing.current = isEditing;
  }, [isEditing]);
  var onCancelEdit = function onCancelEdit() {
    setState(value);
    setValidationResponse(void 0);
    setIsEditing(false);
    setAriaLabelPrefix(t("ActivateToEdit.canceledPrefix"));
    if (onCancel) {
      onCancel();
    }
  };
  var onConfirmEdit = function onConfirmEdit() {
    var response = onValidate ? onValidate(state) : void 0;
    setValidationResponse(response);
    if (response) {
      return;
    }
    setIsEditing(false);
    setAriaLabelPrefix(t("ActivateToEdit.confirmedPrefix"));
    if (onChange) {
      onChange(state);
    }
    onConfirm(state);
  };
  var onStartEdit = function onStartEdit() {
    setAriaLabelPrefix(void 0);
    setIsEditing(true);
  };
  var content;
  if (!isEditing) {
    content = /*#__PURE__*/jsx(ActivateToEditReadView, {
      ariaLabelPrefix: ariaLabelPrefix,
      onBlur: function onBlur() {
        return setAriaLabelPrefix(void 0);
      },
      onStartEdit: onStartEdit,
      readRef: readViewFocusRef,
      readView: readView,
      readOnly: readOnly,
      disabled: disabled,
      hasPlaceholder: hasPlaceholder
    });
  } else {
    content = /*#__PURE__*/jsx(ActivateToEditWriteView, {
      "aria-label": ariaLabelWriteView,
      onCancelEdit: onCancelEdit,
      onConfirmEdit: onConfirmEdit,
      hasValidationError: !!validationResponse,
      children: writeView({
        state: state,
        setState: setState,
        focusRef: focusRef,
        validationResponse: validationResponse
      })
    });
  }
  return /*#__PURE__*/jsx(MicroToast, {
    content: validationResponse && /*#__PURE__*/jsx(ActivateToEditValidationContainer, {
      message: validationResponse.message
    }),
    duration: 0,
    showing: !!validationResponse,
    variant: "error",
    placement: "bottom-start",
    "data-testid": A2E_DATA_TESTIDS.microToastContainer,
    children: /*#__PURE__*/jsx("div", {
      ref: containerRef,
      "data-testid": A2E_DATA_TESTIDS.container,
      onBlur: function onBlur(event) {
        var _containerRef$current;
        if ((_containerRef$current = containerRef.current) !== null && _containerRef$current !== void 0 && _containerRef$current.contains(event.relatedTarget)) {
          return;
        }
        if (_onBlur) {
          _onBlur(event);
        }
      },
      children: content
    })
  });
};
ActivateToEdit.displayName = "ActivateToEdit";

export { ActivateToEdit };
